import React, { useState, useRef } from 'react';
import { api } from '../api';

const FIELD_LABELS = {
  phone: 'Phone Number',
  first_name: 'First Name',
  last_name: 'Last Name',
  address: 'Address',
  city: 'City',
  state: 'State',
  skip: '-- Skip Column --',
};

const FUZZY_ALIASES = {
  phone: ['phone','phonenumber','phone_number','mobile','mobilenumber','cell','cellphone','number','num','no','tel','telephone','phoneno','contactnumber','contact','primaryphone','homephone','workphone','did','ani','callerid'],
  first_name: ['firstname','fname','first','givenname','forename','name1'],
  last_name: ['lastname','lname','last','surname','familyname','name2'],
  address: ['address','addr','add','street','streetaddress','streetaddr','address1','addr1','line1','mailingaddress','homeaddress'],
  city: ['city','town','suburb','municipality','locality'],
  state: ['state','province','region','st','prov','statecode'],
};

function normHeader(h) {
  return h.toLowerCase().replace(/[\s_\-\.#]+/g, '');
}

function detectField(header) {
  const n = normHeader(header);
  for (const [field, aliases] of Object.entries(FUZZY_ALIASES)) {
    if (aliases.includes(n)) return field;
  }
  return 'skip';
}

function LeadsModal({ campaign, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [leadsCount, setLeadsCount] = useState(0);

  // preview step
  const [step, setStep] = useState(1); // 1=pick file, 2=map columns
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [colMapping, setColMapping] = useState([]);
  const [hasHeaderRow, setHasHeaderRow] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const ACCEPTED = '.csv,.xls,.xlsx';

  // ---- parse file client-side for preview ----
  const parsePreview = async (f) => {
    const isXls = /\.(xlsx|xls)$/i.test(f.name);
    if (isXls) {
      // For XLS we can't easily parse client-side, just show filename and skip to upload
      setPreviewHeaders([]);
      setPreviewRows([]);
      setColMapping([]);
      return;
    }
    const text = await f.text().catch(() => null);
    if (!text) return;
    const lines = text.split(/\r?\n/).filter(l => l.trim()).slice(0, 6);
    if (!lines.length) return;
    const parse = (line) => {
      const result = [];
      let cur = '', inQ = false;
      for (const ch of line) {
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === ',' && !inQ) { result.push(cur.trim()); cur = ''; continue; }
        cur += ch;
      }
      result.push(cur.trim());
      return result;
    };
    const header = parse(lines[0]);
    const rows = lines.slice(1).map(parse);
    setPreviewHeaders(header);
    setPreviewRows(rows);
    setColMapping(header.map(detectField));
  };

  const handleFileChange = async (f) => {
    if (!f) return;
    setFile(f);
    setError('');
    setUploadSuccess(false);
    await parsePreview(f);
    setStep(2);
  };

  const handleInputChange = (e) => handleFileChange(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileChange(f);
  };

  const handleUpload = async () => {
    if (!file) { setError('Please select a file'); return; }
    setUploading(true);
    setError('');
    try {
      const result = await api.uploadLeads(campaign.id, file);
      const updatedCampaign = await api.getCampaign(campaign.id);
      setLeadsCount(updatedCampaign.stats?.total_leads ?? result.uploaded);
      setUploadSuccess(true);
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const phoneColDetected = colMapping.includes('phone');

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '620px', width: '95%' }}>

        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">
            Upload Leads — {campaign.name}
            {step === 2 && file && <span style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: '0.5rem', color: 'hsl(var(--muted-foreground))' }}>Step 2 of 2: Map Columns</span>}
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message" data-testid="upload-error">{error}</div>}

        {uploadSuccess ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'hsl(var(--success, 142 76% 36%))' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
            <div style={{ fontWeight: 600 }}>Successfully uploaded {leadsCount} leads!</div>
          </div>
        ) : step === 1 ? (
          <>
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              Supports CSV, XLS and XLSX. Headers are auto-detected — phone numbers are always the priority column.
            </p>
            {/* Drop zone */}
            <div
              data-testid="drop-zone"
              style={{
                border: `2px dashed ${isDragging ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                borderRadius: '0.5rem',
                padding: '2.5rem',
                textAlign: 'center',
                cursor: 'pointer',
                background: isDragging ? 'hsl(var(--primary) / 0.05)' : 'transparent',
                transition: 'all 0.2s',
                marginBottom: '1rem',
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED}
                onChange={handleInputChange}
                style={{ display: 'none' }}
                data-testid="input-csv-file"
              />
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📂</div>
              <p style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Click to select or drag & drop</p>
              <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>CSV, XLS, XLSX — up to 20 MB</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            {/* Step 2: column mapping */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                File: <strong>{file.name}</strong>
              </span>
              <button
                style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}
                onClick={() => { setFile(null); setStep(1); setPreviewHeaders([]); setPreviewRows([]); setColMapping([]); }}
              >
                Change file
              </button>
            </div>

            {previewHeaders.length > 0 ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeaderRow}
                    onChange={e => setHasHeaderRow(e.target.checked)}
                    data-testid="chk-has-header"
                  />
                  <label htmlFor="hasHeader" style={{ fontSize: '0.875rem' }}>First row is a header</label>
                </div>

                {/* Column mapping table */}
                <div style={{ overflowX: 'auto', marginBottom: '0.75rem' }}>
                  <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'hsl(var(--muted) / 0.5)' }}>
                        {previewHeaders.map((h, i) => (
                          <th key={i} style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid hsl(var(--border))' }}>
                            <select
                              value={colMapping[i] || 'skip'}
                              onChange={e => {
                                const m = [...colMapping];
                                m[i] = e.target.value;
                                setColMapping(m);
                              }}
                              style={{ width: '100%', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))', borderRadius: '4px', padding: '2px 4px', fontSize: '0.75rem' }}
                              data-testid={`col-map-${i}`}
                            >
                              {Object.entries(FIELD_LABELS).map(([v, l]) => (
                                <option key={v} value={v}>{l}</option>
                              ))}
                            </select>
                            <div style={{ color: 'hsl(var(--muted-foreground))', marginTop: '2px', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '110px' }}>{h}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.slice(0, 3).map((row, ri) => (
                        <tr key={ri} style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }}>
                          {previewHeaders.map((_, ci) => (
                            <td key={ci} style={{ padding: '0.3rem 0.5rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '110px' }}>
                              {row[ci] || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!phoneColDetected && (
                  <div style={{ padding: '0.5rem 0.75rem', background: 'hsl(var(--warning, 38 92% 50%) / 0.1)', border: '1px solid hsl(var(--warning, 38 92% 50%))', borderRadius: '0.4rem', fontSize: '0.8rem', marginBottom: '0.75rem', color: 'hsl(var(--foreground))' }}>
                    No column mapped to <strong>Phone Number</strong>. Please assign one above, or the backend will scan all cells for phone numbers.
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '0.75rem', background: 'hsl(var(--muted) / 0.3)', borderRadius: '0.4rem', fontSize: '0.875rem', marginBottom: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                XLS/XLSX detected — column mapping will be handled automatically on upload. Phone numbers, names, address, city, state and zip are auto-detected.
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={uploading}
                data-testid="btn-upload-leads"
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LeadsModal;
